import React, { useCallback } from 'react';

import { styled } from '@compiled/react';
import { FormattedMessage } from 'react-intl-next';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import LogOutIcon from '@atlaskit/icon/core/log-out';
import SettingsIcon from '@atlaskit/icon/core/migration/settings';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import PersonIcon from '@atlaskit/icon/core/person';
import { LinkItem, type LinkItemProps, MenuGroup, Section } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import type { ContentProps } from '@atlaskit/popup';
import { ThemeSwitcher as DesignSystemThemeSwitcher } from '@atlassian/theme-switcher';

import { NAVIGATION_CHANNEL } from '../../analytics';
import { AccountDetails } from '../account-details';
import type { AccountMenuProps } from '../account-menu';
import { ThemeSwitcher } from '../theme-switcher';

export type AccountMenuContentProps = {
	closePopup: ContentProps['onClose'];
} & Pick<
	AccountMenuProps,
	| 'testId'
	| 'name'
	| 'picture'
	| 'email'
	| 'managedBy'
	| 'manageProfileLink'
	| 'viewProfileLink'
	| 'onViewProfileClick'
	| 'switchAccountLink'
	| 'logOutLink'
	| 'onThemeChange'
	| 'currentTheme'
	| 'UNSAFE_accountTheme'
	| 'extraMenuItems'
	| 'onLogoutClick'
>;

export const AccountMenuContent: React.ComponentType<AccountMenuContentProps> = ({
	testId,
	closePopup,
	name,
	picture,
	email,
	managedBy,
	manageProfileLink,
	viewProfileLink,
	onViewProfileClick,
	switchAccountLink,
	logOutLink,
	onThemeChange,
	currentTheme,
	UNSAFE_accountTheme,
	extraMenuItems = null,
	onLogoutClick,
}) => {
	const themeSwitcherProps = onThemeChange && currentTheme;
	const shouldRenderProfileThemeSwitcherSection = Boolean(
		viewProfileLink || manageProfileLink || themeSwitcherProps,
	);

	return (
		<Container data-testid={testId}>
			<MenuGroup>
				<Section>
					<AccountDetails
						testId={testId && `${testId}--details`}
						name={name}
						picture={picture}
						email={email}
						managedBy={managedBy}
					/>
				</Section>
				{shouldRenderProfileThemeSwitcherSection && (
					<Section>
						{viewProfileLink && (
							<ViewProfileLink
								onClick={onViewProfileClick}
								testId={testId && `${testId}--view-profile`}
								href={viewProfileLink}
								closePopup={closePopup}
							/>
						)}
						{manageProfileLink && (
							<ManageProfileLink
								testId={testId && `${testId}--manage-profile`}
								href={manageProfileLink}
								closePopup={closePopup}
							/>
						)}
						{themeSwitcherProps && (
							fg('replace-account-menu-theme-switcher') ? (
								<DesignSystemThemeSwitcher
									testId={testId && `${testId}--theme-switcher`}
									onThemeChange={onThemeChange}
									currentColorMode={currentTheme}
									accountColorMode={UNSAFE_accountTheme}
									appearancePreferencesLink={
										UNSAFE_accountTheme !== undefined
											? manageProfileLink?.replace(
													/(%2F|\/)manage-profile[^&]*/g,
													'$1manage-profile$1appearance-preferences',
												)
											: undefined
									}
								/>
							) : (
							<ThemeSwitcher
								testId={testId && `${testId}--theme-switcher`}
								onThemeChange={onThemeChange}
								currentTheme={currentTheme}
								UNSAFE_accountTheme={UNSAFE_accountTheme}
								appearancePreferencesLink={
									UNSAFE_accountTheme !== undefined
										? manageProfileLink?.replace(
												/(%2F|\/)manage-profile[^&]*/g,
												'$1manage-profile$1appearance-preferences',
											)
										: undefined
								}
							/>
							)
						)}
					</Section>
				)}
				{extraMenuItems}
				<Section hasSeparator={shouldRenderProfileThemeSwitcherSection}>
					{switchAccountLink && (
						<SwitchAccountLink
							testId={testId && `${testId}--switch-account`}
							href={switchAccountLink}
							closePopup={closePopup}
						/>
					)}
					<LogOutLink
						testId={testId && `${testId}--log-out`}
						href={logOutLink}
						closePopup={closePopup}
						onClick={onLogoutClick}
					/>
				</Section>
			</MenuGroup>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	boxSizing: 'border-box',
	minWidth: '304px',
});

const ViewProfileLink = ({
	testId,
	href,
	onClick,
	closePopup,
}: Pick<React.ComponentProps<typeof Link>, 'testId' | 'href' | 'onClick' | 'closePopup'>) => (
	<Link
		testId={testId}
		href={href}
		onClick={onClick}
		icon={<PersonIcon color="currentColor" label="" />}
		closePopup={closePopup}
		actionSubjectId="viewProfileLink"
	>
		<FormattedMessage
			id="identity.account-menu.account-details.view-profile-tooltip"
			defaultMessage="Profile"
			description="Text shown when hovering over profile information"
		/>
	</Link>
);

const ManageProfileLink = ({
	testId,
	href,
	closePopup,
}: Pick<React.ComponentProps<typeof Link>, 'testId' | 'href' | 'closePopup'>) => (
	<Link
		testId={testId}
		href={href}
		icon={<SettingsIcon color="currentColor" label="" LEGACY_size="medium" />}
		closePopup={closePopup}
		actionSubjectId="manageProfileLink"
		target="_blank"
		rel="noopener"
	>
		<FormattedMessage
			id="identity.account-menu.account-details.manage-profile-link"
			defaultMessage="Account settings"
			description="Text for link to account management page"
		/>
	</Link>
);

const SwitchAccountLink = ({
	testId,
	href,
	closePopup,
}: Pick<React.ComponentProps<typeof Link>, 'testId' | 'href' | 'closePopup'>) => (
	<Link
		testId={testId}
		icon={<PeopleGroupIcon color="currentColor" label="" />}
		href={href}
		closePopup={closePopup}
		actionSubjectId="switchAccountLink"
	>
		<FormattedMessage
			id="identity.account-menu.account-menu-content.switch-account-button"
			defaultMessage="Switch account"
			description="Label for button in account menu to switch to another another account"
		/>
	</Link>
);

const LogOutLink = ({
	testId,
	href,
	closePopup,
	onClick,
}: Pick<React.ComponentProps<typeof Link>, 'testId' | 'href' | 'closePopup' | 'onClick'>) => (
	<Link
		testId={testId}
		icon={<LogOutIcon color="currentColor" label="" />}
		href={href}
		closePopup={closePopup}
		actionSubjectId="logOutLink"
		onClick={onClick}
	>
		<FormattedMessage
			id="identity.account-menu.account-menu-content.log-out-button"
			defaultMessage="Log out"
			description="Label for button in account menu to log out"
		/>
	</Link>
);

const Link = ({
	testId,
	closePopup,
	icon,
	href,
	onClick,
	actionSubjectId,
	children,
	...props
}: Omit<LinkItemProps, 'onClick'> & {
	actionSubjectId: string;
	children: React.ReactChild;
	closePopup: AccountMenuContentProps['closePopup'];
	href: string;
	icon: JSX.Element;
	onClick?:
		| AccountMenuContentProps['onViewProfileClick']
		| AccountMenuContentProps['onLogoutClick'];
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick: NonNullable<LinkItemProps['onClick']> = useCallback(
		(event) => {
			const analyticsEvent = createAnalyticsEvent({
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubject: 'menuLinkItem',
				actionSubjectId,
			});

			const clone = analyticsEvent.clone();
			if (clone) {
				clone.fire(NAVIGATION_CHANNEL);
			}

			onClick?.(event, analyticsEvent);

			closePopup?.(event);
		},
		[createAnalyticsEvent, onClick, closePopup, actionSubjectId],
	);

	return (
		<LinkItem testId={testId} iconBefore={icon} href={href} onClick={handleClick} {...props}>
			{children}
		</LinkItem>
	);
};
